import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Play, Calendar, TrendingUp, Clock, Users, BarChart3, Activity, DollarSign, AlertTriangle, Globe, Target, Send } from 'lucide-react';
import { formatUSD, formatPercent, formatNum, getChangeColor } from '@/lib/format';
import NeoAgentPanel from '@/components/NeoAgentPanel';
import { LiquidationRiskCard } from '@/components/LiquidationRiskCard';

interface Report {
  id: string;
  report_type: string;
  content: string;
  created_at: string;
  market_data?: any;
}

interface Execution {
  id: string;
  report_type: string;
  executed_at: string;
  success: boolean;
  data_collection: string;
  report_generation: string;
  error_message?: string;
}

export default function AlphaOnChain() {
  const [reports, setReports] = useState<Report[]>([]);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [enrichedData, setEnrichedData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSendingDigest, setIsSendingDigest] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadReports();
    loadExecutions();
    loadLatestEnrichedData();
  }, []);

  const loadReports = async () => {
    try {
      const { data, error } = await supabase
        .from('alpha_onchain_reports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setReports(data || []);
      
      // Extract enriched data from the latest report
      if (data && data.length > 0) {
        const marketData = typeof data[0].market_data === 'string' 
          ? JSON.parse(data[0].market_data) 
          : data[0].market_data;
        if (marketData?.enriched) {
          setEnrichedData(marketData.enriched);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
    }
  };

  const loadLatestEnrichedData = async () => {
    try {
      const { data, error } = await supabase
        .from('alpha_onchain_reports')
        .select('market_data')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.log('No enriched data found yet');
        return;
      }
      
      if (data?.market_data) {
        const marketData = typeof data.market_data === 'string' 
          ? JSON.parse(data.market_data) 
          : data.market_data;
        if (marketData?.enriched) {
          setEnrichedData(marketData.enriched);
        }
      }
    } catch (error) {
      console.log('Error loading enriched data:', error);
    }
  };

  const loadExecutions = async () => {
    try {
      const { data, error } = await supabase
        .from('alpha_onchain_executions')
        .select('*')
        .order('executed_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setExecutions(data || []);
    } catch (error) {
      console.error('Erro ao carregar execuções:', error);
    }
  };

  const collectData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('alpha-onchain-data', {
        body: JSON.stringify({ type: 'all' })
      });

      if (error) throw error;

      toast({
        title: "✅ Dados coletados com sucesso",
        description: "Dados de mercado atualizados e cacheados.",
      });

      console.log('Dados coletados:', data);
    } catch (error) {
      console.error('Erro ao coletar dados:', error);
      toast({
        title: "❌ Erro ao coletar dados",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateReport = async (reportType: 'daily' | 'weekly' | 'preview') => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('alpha-onchain-reports', {
        body: JSON.stringify({ 
          reportType,
          sendToTelegram: true 
        })
      });

      if (error) throw error;

      toast({
        title: `✅ Relatório ${reportType} gerado!`,
        description: "Relatório criado e enviado para o Telegram.",
      });

      loadReports();
      loadExecutions();
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast({
        title: "❌ Erro ao gerar relatório",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const testScheduler = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('alpha-onchain-scheduler');

      if (error) throw error;

      toast({
        title: "🕐 Scheduler testado",
        description: "Função de agendamento executada manualmente.",
      });

      loadExecutions();
    } catch (error) {
      console.error('Erro ao testar scheduler:', error);
      toast({
        title: "❌ Erro no scheduler",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testTelegramSend = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('telegram-test-report', {
        body: JSON.stringify({ 
          reportType: 'daily',
          testMode: true 
        })
      });

      if (error) throw error;

      toast({
        title: "📱 Teste enviado!",
        description: "Relatório de teste enviado para seu Telegram pessoal.",
      });

      loadReports();
    } catch (error) {
      console.error('Erro ao enviar teste:', error);
      toast({
        title: "❌ Erro no teste Telegram",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const sendDailyDigestManual = async () => {
    setIsSendingDigest(true);
    try {
      const { error } = await supabase.functions.invoke('daily-digest-scheduler', {
        body: JSON.stringify({ manual: true })
      });

      if (error) throw error;

      toast({
        title: '📰 Daily Digest enviado',
        description: 'O digest foi gerado e enviado para o Telegram.'
      });
    } catch (error) {
      console.error('Erro ao enviar Daily Digest:', error);
      toast({
        title: '❌ Erro ao enviar Daily Digest',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsSendingDigest(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getReportTypeLabel = (type: string) => {
    const labels = {
      daily: 'Diário',
      weekly: 'Semanal',
      preview: 'Pré-semana'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getStatusColor = (success: boolean) => {
    return success ? 'bg-green-500' : 'bg-red-500';
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Hero Section */}
        <div className="space-y-3 fade-in-up">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gradient flex items-center gap-3">
                Alpha On-Chain
              </h1>
              <p className="text-muted-foreground text-lg mt-2 max-w-xl">
                Sistema de relatórios automatizados de mercado cripto
              </p>
              <div className="gradient-line w-24 mt-3" />
            </div>
            <Badge className="text-sm px-4 py-2 bg-success/10 text-success border-success/20">
              <span className="h-2 w-2 rounded-full bg-success animate-pulse mr-2" />
              Sistema Ativo
            </Badge>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 fade-in-up delay-100">
          <Button
            onClick={collectData}
            disabled={isLoading}
            className="h-11"
            variant="outline"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            {isLoading ? 'Coletando...' : 'Coletar Dados'}
          </Button>
          
          <Button
            onClick={() => generateReport('daily')}
            disabled={isGenerating}
            className="h-11"
          >
            <Calendar className="mr-2 h-4 w-4" />
            Diário
          </Button>
          
          <Button
            onClick={() => generateReport('weekly')}
            disabled={isGenerating}
            className="h-11"
            variant="secondary"
          >
            <Calendar className="mr-2 h-4 w-4" />
            Semanal
          </Button>
          
          <Button
            onClick={testTelegramSend}
            disabled={isGenerating}
            className="h-11"
            variant="default"
          >
            📱 Telegram
          </Button>
          
          <Button
            onClick={testScheduler}
            disabled={isLoading}
            className="h-11"
            variant="outline"
          >
            <Clock className="mr-2 h-4 w-4" />
            Scheduler
          </Button>

          <Button
            onClick={sendDailyDigestManual}
            disabled={isSendingDigest}
            className="h-11"
            variant="secondary"
          >
            <Send className="mr-2 h-4 w-4" />
            {isSendingDigest ? 'Enviando...' : 'Daily Digest'}
          </Button>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 fade-in-up delay-200">
          <Card className="glass-card hover-scale">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Próximo Relatório</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Diário</div>
              <p className="text-xs text-muted-foreground">
                Amanhã às 08:30
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card hover-scale">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Relatórios Enviados</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <Users className="h-4 w-4 text-accent" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reports.length}</div>
              <p className="text-xs text-muted-foreground">
                Total gerados
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card hover-scale">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-success/10 flex items-center justify-center">
                <Play className="h-4 w-4 text-success" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">
                {executions.length > 0 
                  ? Math.round((executions.filter(e => e.success).length / executions.length) * 100)
                  : 0}%
              </div>
              <p className="text-xs text-muted-foreground">
                {executions.filter(e => e.success).length} de {executions.length} execuções
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="fade-in-up delay-300">
        <Tabs defaultValue="resumo" className="space-y-4">
          <TabsList className="glass-card grid w-full grid-cols-4 lg:grid-cols-8 h-auto p-1">
            <TabsTrigger value="resumo" className="text-xs sm:text-sm">Resumo</TabsTrigger>
            <TabsTrigger value="movers" className="text-xs sm:text-sm">Movers</TabsTrigger>
            <TabsTrigger value="derivatives" className="text-xs sm:text-sm">Derivativos</TabsTrigger>
            <TabsTrigger value="tvl" className="text-xs sm:text-sm">TVL</TabsTrigger>
            <TabsTrigger value="valuation" className="text-xs sm:text-sm">Valuation</TabsTrigger>
            <TabsTrigger value="social" className="text-xs sm:text-sm">Social</TabsTrigger>
            <TabsTrigger value="risk" className="text-xs sm:text-sm">Risco</TabsTrigger>
            <TabsTrigger value="neo-agent" className="text-xs sm:text-sm">Neo</TabsTrigger>
          </TabsList>

          {/* Resumo Tab */}
          <TabsContent value="resumo" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Relatórios Gerados</CardTitle>
                <CardDescription>
                  Últimos relatórios enviados aos usuários do Telegram
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {reports.map((report) => (
                      <div key={report.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <Badge variant="secondary">
                            {getReportTypeLabel(report.report_type)}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(report.created_at)}
                          </span>
                        </div>
                        <div className="bg-muted rounded p-3 mt-2">
                          <pre className="text-sm whitespace-pre-wrap font-mono">
                            {report.content.substring(0, 500)}
                            {report.content.length > 500 && '...'}
                          </pre>
                        </div>
                      </div>
                    ))}
                    {reports.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        Nenhum relatório gerado ainda. Clique em "Relatório Diário" para testar.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Movers Tab */}
          <TabsContent value="movers" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    Top Gainers 24h
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {enrichedData?.gainers_losers_24h?.gainers ? (
                    <div className="space-y-2">
                      {enrichedData.gainers_losers_24h.gainers.slice(0, 5).map((asset: any, i: number) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="font-medium">{asset.symbol?.toUpperCase() || asset.name}</span>
                          <span className={getChangeColor(asset.price_change_percentage_24h)}>
                            {formatPercent(asset.price_change_percentage_24h)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Dados não disponíveis</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-red-500 rotate-180" />
                    Top Losers 24h
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {enrichedData?.gainers_losers_24h?.losers ? (
                    <div className="space-y-2">
                      {enrichedData.gainers_losers_24h.losers.slice(0, 5).map((asset: any, i: number) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="font-medium">{asset.symbol?.toUpperCase() || asset.name}</span>
                          <span className={getChangeColor(asset.price_change_percentage_24h)}>
                            {formatPercent(asset.price_change_percentage_24h)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Dados não disponíveis</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Derivatives Tab */}
          <TabsContent value="derivatives" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {enrichedData?.derivatives_summary ? (
                Object.entries(enrichedData.derivatives_summary).map(([symbol, data]: [string, any]) => (
                  <Card key={symbol}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5" />
                        {symbol.toUpperCase()} Derivatives
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span>Funding Rate</span>
                        <span className={getChangeColor(data.funding_now)}>{formatPercent(data.funding_now, 4)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Funding 7d Avg</span>
                        <span>{formatPercent(data.funding_7d_avg, 4)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Open Interest</span>
                        <span>{formatUSD(data.oi_now_usd)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>OI Change 24h</span>
                        <span className={getChangeColor(data.oi_delta_24h)}>{formatPercent(data.oi_delta_24h)}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-muted-foreground col-span-2">Dados de derivativos não disponíveis</p>
              )}
            </div>
          </TabsContent>

          {/* TVL & Fees Tab */}
          <TabsContent value="tvl" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Top Protocols by TVL 7d
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {enrichedData?.tvl_changes_7d_protocols ? (
                    <div className="space-y-2">
                      {enrichedData.tvl_changes_7d_protocols.slice(0, 5).map((protocol: any, i: number) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="font-medium">{protocol.name}</span>
                          <div className="text-right">
                            <div>{formatUSD(protocol.tvl)}</div>
                            <div className="text-sm text-muted-foreground">
                              {formatPercent(protocol.change_7d)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Dados não disponíveis</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Top Fees 7d
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {enrichedData?.fees_top_7d ? (
                    <div className="space-y-2">
                      {enrichedData.fees_top_7d.slice(0, 5).map((protocol: any, i: number) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="font-medium">{protocol.name}</span>
                          <div className="text-right">
                            <div>{formatUSD(protocol.total_fees_7d)}</div>
                            <div className="text-sm text-muted-foreground">
                              7d fees
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Dados não disponíveis</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Valuation Tab */}
          <TabsContent value="valuation" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-green-500" />
                    Cheap by FDV/TVL
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {enrichedData?.valuation_snaps?.cheap_fdv_tvl ? (
                    <div className="space-y-2">
                      {enrichedData.valuation_snaps.cheap_fdv_tvl.slice(0, 5).map((asset: any, i: number) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="font-medium">{asset.symbol?.toUpperCase() || asset.name}</span>
                          <span className="text-green-600">{formatNum(asset.fdv_tvl_ratio, 1)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Dados não disponíveis</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-red-500" />
                    Expensive by FDV/TVL
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {enrichedData?.valuation_snaps?.expensive_fdv_tvl ? (
                    <div className="space-y-2">
                      {enrichedData.valuation_snaps.expensive_fdv_tvl.slice(0, 5).map((asset: any, i: number) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="font-medium">{asset.symbol?.toUpperCase() || asset.name}</span>
                          <span className="text-red-600">{formatNum(asset.fdv_tvl_ratio, 1)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Dados não disponíveis</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Social & News Tab */}
          <TabsContent value="social" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Top News
                </CardTitle>
              </CardHeader>
              <CardContent>
                {enrichedData?.news_top ? (
                  <div className="space-y-4">
                    {enrichedData.news_top.slice(0, 3).map((news: any, i: number) => (
                      <div key={i} className="border rounded-lg p-3">
                        <div className="flex justify-between items-start mb-2">
                          <Badge variant={news.severity === 'high' ? 'destructive' : news.severity === 'med' ? 'default' : 'secondary'}>
                            {news.severity?.toUpperCase() || 'MEDIUM'}
                          </Badge>
                          <span className="text-sm text-muted-foreground">{news.source}</span>
                        </div>
                        <h4 className="font-medium mb-1">{news.title}</h4>
                        {news.interpretation && (
                          <p className="text-sm text-muted-foreground">{news.interpretation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">Dados não disponíveis</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Risk Tab */}
          <TabsContent value="risk" className="space-y-4">
            {/* Existing Risk Signals Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Risk Signals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {enrichedData?.risk_signals ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>Overall Risk Level</span>
                      <Badge variant={
                        enrichedData.risk_signals.level === 'high' ? 'destructive' :
                        enrichedData.risk_signals.level === 'medium' ? 'default' : 'secondary'
                      }>
                        {enrichedData.risk_signals.level?.toUpperCase()}
                      </Badge>
                    </div>
                    {enrichedData.risk_signals.funding_risk && (
                      <div className="flex justify-between items-center">
                        <span>Funding Risk</span>
                        <Badge variant="outline">{enrichedData.risk_signals.funding_risk}</Badge>
                      </div>
                    )}
                    {enrichedData.risk_signals.oi_risk && (
                      <div className="flex justify-between items-center">
                        <span>Open Interest Risk</span>
                        <Badge variant="outline">{enrichedData.risk_signals.oi_risk}</Badge>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">Dados não disponíveis</p>
                )}
              </CardContent>
            </Card>

            {/* DeFi Liquidation Risk - New Section */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                DeFi Liquidation Risk
              </h3>
              <p className="text-sm text-muted-foreground">
                Monitor protocol health, utilization rates, and liquidation events that can trigger cascading price drops.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LiquidationRiskCard protocol="aave-v3" />
              <LiquidationRiskCard protocol="compound-v3" />
            </div>
          </TabsContent>

          <TabsContent value="executions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Log de Execuções</CardTitle>
                <CardDescription>
                  Histórico de execuções automáticas e manuais
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {executions.map((execution) => (
                      <div key={execution.id} className="flex items-center justify-between border rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${getStatusColor(execution.success)}`} />
                          <div>
                            <div className="font-medium">
                              {getReportTypeLabel(execution.report_type)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {formatDate(execution.executed_at)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          <div>Data: {execution.data_collection}</div>
                          <div>Report: {execution.report_generation}</div>
                        </div>
                      </div>
                    ))}
                    {executions.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        Nenhuma execução registrada ainda.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Configuração de Agendamento</CardTitle>
                <CardDescription>
                  Cron jobs configurados para execução automática
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium">Relatório Diário</h4>
                        <p className="text-sm text-muted-foreground">
                          Segunda a Sexta, 08:30 AM
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        Ativo
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium">Relatório Semanal</h4>
                        <p className="text-sm text-muted-foreground">
                          Sexta-feira, 16:30 PM
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        Ativo
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium">Relatório Pré-semana</h4>
                        <p className="text-sm text-muted-foreground">
                          Domingo, 17:00 PM
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        Ativo
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="neo-agent" className="space-y-6">
            <NeoAgentPanel />
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </div>
  );
}